import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/api";
import { Creator } from "@/types";

const TINTS = ["mint", "lavender", "butter", "pink"];
const EMPTY = { name: "", niche: "", metric: "", tint: "mint", featured: false, published: true };
type CreatorForm = typeof EMPTY;

const inputCls = "w-full border border-stone-300 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none";
const btnCls = "border border-stone-300 px-2 py-1 text-xs font-semibold hover:bg-stone-50";

export default function AdminCreators() {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<CreatorForm>(EMPTY);

  const load = () => api.get("/admin/creators").then((r) => setCreators(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(EMPTY); setEditing("new"); };
  const startEdit = (c: Creator) => {
    setForm({ name: c.name, niche: c.niche, metric: c.metric, tint: c.tint, featured: c.featured, published: c.published });
    setEditing(c.id);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing === "new") await api.post("/admin/creators", form);
      else await api.put(`/admin/creators/${editing}`, form);
      toast.success("Creator saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (c: Creator) => {
    if (!window.confirm(`Delete ${c.name} and all their videos?`)) return;
    try {
      await api.delete(`/admin/creators/${c.id}`);
      toast.success("Creator deleted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = async (c: Creator, key: "featured" | "published") => {
    try {
      await api.put(`/admin/creators/${c.id}`, {
        name: c.name, niche: c.niche, metric: c.metric, tint: c.tint,
        featured: key === "featured" ? !c.featured : c.featured,
        published: key === "published" ? !c.published : c.published,
      });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const move = async (i: number, dir: -1 | 1) => {
    const ids = creators.map((c) => c.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setCreators(ids.map((id) => creators.find((c) => c.id === id)!).sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id)));
    try { await api.put("/admin/creators-reorder", { ids }); } catch (e) { toast.error(errMsg(e)); load(); }
  };

  return (
    <div data-testid="admin-creators">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-bold text-stone-900">Creators ({creators.length})</h2>
        <button data-testid="admin-add-creator" onClick={startNew} className="flex items-center gap-1 bg-stone-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-stone-700">
          <Plus size={13} /> Add creator
        </button>
      </div>

      {editing && (
        <div data-testid="creator-form" className="mb-5 border border-stone-300 bg-stone-50 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Name</label>
              <input data-testid="creator-form-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Niche</label>
              <input data-testid="creator-form-niche" className={inputCls} value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })} placeholder="Tech & Gadgets" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Metric line</label>
              <input data-testid="creator-form-metric" className={inputCls} value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })} placeholder="890K SUBS · 3.1M AVG VIEWS" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Tint</label>
              <select data-testid="creator-form-tint" className={inputCls} value={form.tint} onChange={(e) => setForm({ ...form, tint: e.target.value })}>
                {TINTS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold text-stone-700">
              <input data-testid="creator-form-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
              Featured on homepage
            </label>
            <label className="flex items-center gap-2 text-xs font-semibold text-stone-700">
              <input data-testid="creator-form-published" type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
              Published (visible on site)
            </label>
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="creator-form-save" onClick={save} className="bg-stone-900 px-4 py-2 text-xs font-semibold text-white hover:bg-stone-700">Save</button>
            <button data-testid="creator-form-cancel" onClick={() => setEditing(null)} className={btnCls}>Cancel</button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {creators.map((c, i) => (
          <div key={c.id} data-testid={`creator-row-${c.slug}`} className="flex flex-wrap items-center gap-2 border border-stone-200 px-3 py-2.5">
            <div className="flex flex-col">
              <button data-testid={`creator-up-${c.slug}`} onClick={() => move(i, -1)} disabled={i === 0} className="text-stone-400 hover:text-stone-900 disabled:opacity-30"><ArrowUp size={13} /></button>
              <button data-testid={`creator-down-${c.slug}`} onClick={() => move(i, 1)} disabled={i === creators.length - 1} className="text-stone-400 hover:text-stone-900 disabled:opacity-30"><ArrowDown size={13} /></button>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-stone-900">{c.name}</p>
              <p className="truncate text-xs text-stone-500">/{c.slug} · {c.niche} · {c.videos?.length || 0} videos</p>
            </div>
            <button data-testid={`creator-featured-${c.slug}`} onClick={() => toggle(c, "featured")} className={`border px-2 py-1 text-[10px] font-bold ${c.featured ? "border-amber-400 bg-amber-100 text-amber-800" : "border-stone-300 text-stone-500"}`}>
              FEATURED
            </button>
            <button data-testid={`creator-published-${c.slug}`} onClick={() => toggle(c, "published")} className={`border px-2 py-1 text-[10px] font-bold ${c.published ? "border-green-400 bg-green-100 text-green-800" : "border-stone-300 text-stone-400"}`}>
              {c.published ? "LIVE" : "HIDDEN"}
            </button>
            <button data-testid={`creator-edit-${c.slug}`} onClick={() => startEdit(c)} className={btnCls}><Pencil size={13} /></button>
            <button data-testid={`creator-delete-${c.slug}`} onClick={() => del(c)} className="border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
